// @flow
import 'isomorphic-fetch';
import React, { useRef, useEffect } from 'react';
import injectSheet from 'react-jss';
import { Parallax, Background } from 'react-parallax';
import classNames from 'classnames';
import Slot from 'shared/editor/Slot';
import SocialIcon from 'shared/components/SocialIcon';
import translations from 'shared/strings';
import getResponsiveImageUrl from 'shared/lib/getResponsiveImageUrl';
import { formatDate, formatDateCN } from 'shared/lib/dateUtils';
import type { $Slot, $Preset } from 'shared/templates/types';
import type { Link as SocialLink } from 'shared/components/SocialIcon';
import SlideUpFadeIn from 'shared/transitions/SlideUpFadeIn';
import { getAPIDomain, getWebDomain } from 'shared/lib/clientSettings';
import withThemeProvider from 'shared/hoc/withThemeProvider';
import {
  FONT_FAMILY_ROBOTO
} from 'shared/constants';

type HeroImageUrlProps =
  | {
      mobile: string,
      tablet: string,
      dekstop: string,
    }
  | string;

type Tag = {
  tag: string
}

type TemplateSettings = {
  region: 'US' | 'CN',
  section: 'FUTURESIGHT' | 'PRESSROOM',
  url: string,
  title: string,
  heroImageUrl: HeroImageUrlProps,
  featured: Boolean,
  published_at: Date,
  published_by: string,
  social_share: SocialLink[],
  tags: Tag[],
  background_position: string,
  background_scale: string,
  hero_background_color?: string,
  responsive_image?: boolean
}

type Props = {
  classes: Object,
  slots: $Slot[],
  templateSettings: TemplateSettings,
  widgetPresets: $Preset[],
  path: string
}

const FutureSightDetails = (props: Props) => {
  const {
    classes: c,
    templateSettings: {
      background_position = 'CENTER CENTER',
      background_scale = 'cover',
      hero_background_color = '',
      region,
      section,
      url: articleUrl
    },
    templateSettings,
    slots,
    widgetPresets,
    path
  } = props;

  const prevLink = useRef();
  const nextLink = useRef();

  useEffect(() => {
    const fetchDomain = getAPIDomain(region) || '';

    if (section === 'FUTURESIGHT') {
      // Fetch data for Prev/Next links.
      fetch(`${fetchDomain}/api/futuresight/post?path=${articleUrl}&link=PREV&region=${region}`)
        .then(res => res.json())
        .then((res) => {
          prevLink.current.href = res.url;
        })
        .catch(() => {
          prevLink.current.href = `/${String(region).toLowerCase()}/futuresight`;
        });
      fetch(`${fetchDomain}/api/futuresight/post?path=${articleUrl}&link=NEXT&region=${region}`)
        .then(res => res.json())
        .then((res) => {
          nextLink.current.href = res.url;
        })
        .catch(() => {
          nextLink.current.href = `/${String(region).toLowerCase()}/futuresight`;
        });
    }
  }, [prevLink, nextLink, region, section, articleUrl]);

  const iconStyle = {
    fontSize: '28px',
    color: 'white',
    marginLeft: '8px',
    marginRight: '8px',
    textDecoration: 'none',
    cursor: 'pointer'
  };

  const domain = getWebDomain(region);
  const sectionSlug = section === 'FUTURESIGHT'
    ? 'futuresight'
    : section === 'PRESSROOM'
      ? 'press-room'
      : '';

  // Default href before fetch completes (matches useEffect catch fallback); avoids `<a>` without href.
  const futuresightNavFallback = `/${String(region).toLowerCase()}/futuresight`;

  const links = templateSettings.section === 'FUTURESIGHT' && (
  <div className={c.links}>
    <a ref={prevLink} className={c.link} href={futuresightNavFallback}>
      <span className="material-icons">arrow_back</span>
      {translations.PREV_ARTICLE[templateSettings.region]}
    </a>
    <a ref={nextLink} className={c.link} href={futuresightNavFallback}>
      {translations.NEXT_ARTICLE[templateSettings.region]}
      <span className="material-icons">arrow_forward</span>
    </a>
  </div>
  );

  const renderDate = () => (
    <div className={c.date}>
      {
        templateSettings.region === 'CN'
          ? formatDateCN(templateSettings.published_at)
          : formatDate(templateSettings.published_at)
      }
    </div>
  );

  const renderTitle = () => (
    <div>
      <div className={c.divider} />
      <h1 className={c.title}>{templateSettings.title}</h1>
    </div>
  );

  return (
    <div
      className={c.container}
    >
      <div className={c.menu}>
        <Slot id="menu-bar" slots={slots} widgetPresets={widgetPresets} path={path} />
      </div>
      <Parallax strength={200} className={c.parallaxWrapper}>
        <Background className={c.backgroundContainer}>
          <div
            className={c.heroContainer}
            style={{
              backgroundPosition: background_position,
              backgroundSize: background_scale,
              backgroundColor: hero_background_color || 'transparent'
            }}
          />
        </Background>
      </Parallax>
      <div className={c.content}>
        <div className={c.leftCol}>
          <div
            className={c.headerContainer}
          >
            <div className={c.headerContent}>
              {renderDate()}
              {renderTitle()}
              <hr className={c.hr} />
              <div className={c.row}>
                <div className={c.author}>
                  {translations.POSTED_BY[templateSettings.region]}
                  <span className={c.name}>
                      &nbsp;
                    {templateSettings.published_by}
                  </span>
                </div>
                <div className={c.headerSocialIcons}>
                  {templateSettings.social_share.map((link, i) => (
                    <SocialIcon
                      key={i}
                      iconStyle={iconStyle}
                      link={link}
                      generateQRCode={`${domain}${templateSettings.url}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={c.rightCol}>
          {sectionSlug && (
            <div className={c.backLinkContainer}>
              <a id="indexLink" className={c.backLink} href={`/${templateSettings.region.toLowerCase()}/${sectionSlug}`}>
                <span className="material-icons">arrow_back</span>
                {translations.BACK_TO_INDEX[templateSettings.region]}
              </a>
            </div>
          )}
          <Slot id="header-right" slots={slots} widgetPresets={widgetPresets} path={path} />
          {templateSettings.tags.length > 0 && sectionSlug &&
          <div className={c.tags}>
            <div className={c.tagsTitle}>{translations.TAGS[templateSettings.region]}</div>
            {templateSettings.tags.map(tag => (
              <div key={tag.tag}>
                <a
                  className={c.tag}
                  href={`/${templateSettings.region.toLowerCase()}/${sectionSlug}/tags/${tag.tag.toLowerCase()}`}
                >
                  {tag.tag}
                </a>
              </div>
            ))}
          </div>
          }
        </div>
        <div className={classNames(c.leftCol, c.adjustedTop, c.articleCol)}>
          <Slot id="left-col" slots={slots} widgetPresets={widgetPresets} path={path} />
          <SlideUpFadeIn show>
            <div
              className={c.socialShare}
            >
              {templateSettings.social_share.map((link, i) => (
                <SocialIcon
                  key={i}
                  iconStyle={{ ...iconStyle, 'color': 'black' }}
                  link={link}
                  generateQRCode={`${domain}${templateSettings.url}`}
                />
              ))}
            </div>
          </SlideUpFadeIn>
          {links}
        </div>
        <div className={classNames(c.rightCol, c.adjustedTop)}>
          <Slot id="right-col" slots={slots} widgetPresets={widgetPresets} path={path} />
        </div>
      </div>
      <footer id="footer" className={c.footer}>
        <Slot id="footer" slots={slots} widgetPresets={widgetPresets} path={path} />
      </footer>
    </div>
  );
};

// For pages with a single hero image
const getBGImageUrl = (props: Props, size) => {
  const { templateSettings: s } = props;
  const { responsive_image: isResponsive } = s;

  if (!isResponsive) {
    return `url(${s.heroImageUrl})`;
  }

  return getResponsiveImageUrl(s.heroImageUrl, size);
};

// For pages with a cropped hero image (using the cropper tool)
const getCroppedBGImageUrl = (props, device) => {
  const { templateSettings: s } = props;
  return `url(${s.heroImageUrl?.[device]})`;
};

const styles = {
  menu: {
    width: '100%',
    position: 'relative',
    zIndex: '10'
  },
  parallaxWrapper: {
    width: '100%',
    height: '75vh',
    '& .react-parallax-background-children': {
      width: '100%',
      height: '100%'
    },
    '@media(min-width: 1920px)': {
      // Image 1920(max-width) x 1080(max-height)
      marginLeft: 'auto',
      marginRight: 'auto',
      maxHeight: '1440px',
      maxWidth: '2560px',
    },
  },
  backgroundContainer: {
    width: '100%',
    height: '100%'
  },
  heroContainer: {
    // Prevent white lines on left/right
    marginLeft: '-1px',
    width: '101%',
    height: '100%',
    backgroundRepeat: 'no-repeat',
    // Support cropped and uncropped images
    backgroundImage: (props) => {
      const mobileCroppedImage = props?.templateSettings?.heroImageUrl?.mobile;
      const mobileSelectedImage = props?.templateSettings?.heroImageUrlMobile;

      // Prefer manually selected image, then cropped, then responsive
      if (mobileSelectedImage) return `url(${mobileSelectedImage})`;

      return mobileCroppedImage ?
        getCroppedBGImageUrl(props, 'mobile') : getBGImageUrl(props, '800w')
    },
    '@media(min-width: 601px)': {
      backgroundImage: props => props?.templateSettings?.heroImageUrl?.tablet ?
        getCroppedBGImageUrl(props, 'tablet') : getBGImageUrl(props, '1200w'),
    },
    '@media(min-width: 1201px)': {
      backgroundImage: props => props?.templateSettings?.heroImageUrl?.desktop ?
        getCroppedBGImageUrl(props, 'desktop') : getBGImageUrl(props, '1600w'),
    },
    '@media(min-width: 1601px)': {
      backgroundImage: props => !props?.templateSettings?.heroImageUrl?.desktop &&
        getBGImageUrl(props, '1800w'),
    },
    '@media(min-width: 1801px)': {
      backgroundImage: props => !props?.templateSettings?.heroImageUrl?.desktop &&
        `url(${props.templateSettings && props.templateSettings.heroImageUrl})`,
    }
  },
  content: {
    width: '100%',
    maxWidth: '1596px',
    margin: 'auto',
    background: 'white',
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: '36px',
    '@media(max-width: 960px)': {
      marginBottom: '0'
    }
  },
  leftCol: {
    width: '66.7%',
    position: 'relative',
    marginTop: '-72px',
    left: '72px',
    background: 'white',
    '@media(max-width: 960px)': {
      width: '100%',
      left: '0',
      right: '0',
      marginBottom: '36px'
    }
  },
  links: {
    width: '90%',
    paddingLeft: '36px',
    paddingRight: '36px',
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '36px',
    '@media(max-width: 960px)': {
      width: '100%'
    }
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    color: 'black',
    cursor: 'pointer',
    fontSize: '12px',
    textTransform: 'uppercase',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontWeight: '600',
    letterSpacing: '1.2px',
    textDecoration: 'none',
    '& > .material-icons': {
      fontSize: '12px',
      marginLeft: '5px',
      marginRight: '5px'
    }
  },
  headerSocialIcons: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '@media(max-width: 960px)': {
      justifyContent: 'flex-start',
      marginTop: '12px'
    }
  },
  socialShare: {
    marginTop: '36px',
    width: '90%',
    display: 'flex',
    justifyContent: 'center'
  },
  linkContainer: {
    position: 'relative'
  },
  backLinkContainer: {
    paddingBottom: '18px',
    borderBottom: '1px solid #dddddd'
  },
  backLink: {
    color: 'black',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '12px',
    textTransform: 'uppercase',
    fontFamily: FONT_FAMILY_ROBOTO,
    fontWeight: '600',
    letterSpacing: '1.2px',
    textDecoration: 'none',
    '& > .material-icons': {
      fontSize: '12px',
      marginRight: '5px'
    }
  },
  rightCol: {
    width: '33.3%',
    background: '#f7f7f7',
    marginTop: '-36px',
    padding: '36px',
    position: 'relative',
    zIndex: '1',
    '@media(max-width: 960px)': {
      width: '100%'
    }
  },
  footer: {
    width: '100%'
  },
  headerContainer: {
    width: '100%',
    background: 'black',
    padding: '36px',
    color: 'white',
    fontFamily: FONT_FAMILY_ROBOTO,
  },
  headerContent: {
    maxWidth: '90%',
    '@media(max-width: 960px)': {
      maxWidth: '100%'
    }
  },
  date: {
    fontSize: '15px',
    fontWeight: '600',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    lineHeight: '1.2em',
  },
  title: {
    border: '0',
    padding: '0',
    margin: '27px 0 0',
    width: '100%',
    fontSize: '40px',
    fontWeight: '300',
    lineHeight: '1.5em',
    '@media(max-width: 960px)': {
      fontSize: '32px',
      lineHeight: '1.2em'
    },
    '@media(max-width: 575px)': {
      fontSize: '24px',
      lineHeight: '1.25em'
    }
  },
  author: {
    fontSize: '17px',
    lineHeight: '1.2em'
  },
  name: {
    color: '#949599'
  },
  hr: {
    border: 'none',
    borderBottom: '1px solid rgb(48, 49, 51)',
    marginTop: '36px',
    marginBottom: '0'
  },
  divider: {
    width: '90px',
    height: '2px',
    backgroundColor: '#949599',
    marginTop: '36px'
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '36px',
    marginBottom: '18px',
    '@media(max-width: 960px)': {
      flexWrap: 'wrap',
      rowGap: '12px'
    }
  },
  tags: {
    paddingTop: '36px',
    paddingBottom: '36px',
    borderBottom: '1px solid #dddddd'
  },
  tagsTitle: {
    fontSize: '13px',
    letterSpacing: '.1',
    fontWeight: '600',
    color: 'black',
    textTransform: 'uppercase',
    marginBottom: '18px'
  },
  tag: {
    textDecoration: 'none',
    display: 'inline-block',
    padding: '9px 25px',
    color: '#303133',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '.1em',
    fontSize: '10px',
    border: '1px solid #303133',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    marginBottom: '2px',
    '&:hover': {
      backgroundColor: '#303133',
      color: 'white'
    }
  },
  adjustedTop: {
    marginTop: '0',
    paddingTop: '26px'
  },
  articleCol: {
    left: '0px',
    paddingLeft: '72px',
    '@media(max-width: 960px)': {
      paddingLeft: '0px'
    }
  }
};

export default withThemeProvider(injectSheet(styles)(FutureSightDetails));
